import {FC, MutableRefObject, useCallback, useEffect, useRef,} from "react";
import "highlight.js/styles/monokai-sublime.css";
import {Editor} from "@tinymce/tinymce-react";
import {Editor as EDTR} from "tinymce/tinymce";
import {DocumentCard} from "@fluentui/react";
import "../styles/ReactQuillIsolated.css";
import {
    getReactQuillDraftStorageKey,
    normalizeTinyMceContent,
    tinyMceInlineImageUploadHandler,
} from "./ReactQuillIsolatedHelpers";
import {
    readEditorDraft,
    removeEditorDraft,
    writeEditorDraft,
    writeEditorDraftBeforeUnload,
} from "./EditorDraftStore";

export interface IReactQuillContentReplacement {
    content?: string | null;
    requestId: number;
}

interface IReactQuillIsolated {
    backgroundColor: string;
    value?: string | null;
    contentRef?: MutableRefObject<string | null>;
    cacheIdentifier: string;
    hiddenInputProps?: any;
    disabled?: boolean;
    originalMessageColor?: string;
    minHeight?: any;
    className?: string;
    replaceContent?: IReactQuillContentReplacement | null;
    draftKey?: string;
    onContentChange?: (content: string | null) => void;
    onBlurContent?: (content: string | null) => void;
}

const setEditorSurfaceStyle = (
    editor: EDTR,
    backgroundColor: string,
    minHeight: number,
) => {
    const editorBody = editor.getBody();
    if (editorBody) {
        editorBody.style.backgroundColor = backgroundColor;
        editorBody.style.minHeight = `${minHeight}px`;
    }
};

const DRAFT_AUTOSAVE_DELAY_MS = 400;

const writeDraft = (storageKey: string, content: string, beforeUnload = false) => {
    if (!normalizeTinyMceContent(content)) {
        removeEditorDraft(storageKey);
    } else if (beforeUnload) {
        writeEditorDraftBeforeUnload(storageKey, content);
    } else {
        void writeEditorDraft(storageKey, content);
    }
};

const applyEditorContent = (editor: EDTR, content: string) => {
    if (normalizeTinyMceContent(editor.getContent()) !== normalizeTinyMceContent(content)) {
        editor.setContent(content);
        editor.undoManager?.clear();
        editor.undoManager?.add();
    }
};

// Draft keys already offered for restore in this page load. A remounted editor
// (loading states, refresh, reopening a reply) silently reloads the stored draft:
// after the first answer the storage holds only text typed since then.
const offeredDraftKeys = new Set<string>();

const resolveDraftToRestore = (
    draftKey: string,
    initialContent: string,
    draftContent: string | null,
) => {
    const storageKey = getReactQuillDraftStorageKey(draftKey);

    if (
        !normalizeTinyMceContent(draftContent) ||
        normalizeTinyMceContent(draftContent) ===
        normalizeTinyMceContent(initialContent)
    ) {
        if (draftContent !== null) {
            removeEditorDraft(storageKey);
        }
        return null;
    }

    if (offeredDraftKeys.has(draftKey)) {
        return draftContent;
    }

    offeredDraftKeys.add(draftKey);
    if (window.confirm("Postoji nespremljena poruka, želite li je učitati?")) {
        return draftContent;
    }

    removeEditorDraft(storageKey);
    return null;
};

export const ReactQuillIsolated: FC<IReactQuillIsolated> = ({
                                                                backgroundColor,
                                                                originalMessageColor,
                                                                disabled,
                                                                className,
                                                                value,
                                                                hiddenInputProps,
                                                                contentRef,
                                                                minHeight,
                                                                cacheIdentifier,
                                                                replaceContent,
                                                                draftKey,
                                                                onContentChange,
                                                                onBlurContent,
                                                            }) => {
    const editorRef = useRef<EDTR | null>(null);
    const pendingReplacementContent = useRef<string | null>(null);
    const initialValueRef = useRef(value ?? "");
    const initialValueCacheIdentifierRef = useRef(cacheIdentifier);
    const lastValue = useRef(value ?? "");
    const editorMinHeight = minHeight ?? 500;
    const draftSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingDraftRef = useRef<{storageKey: string; content: string} | null>(null);
    const valueRef = useRef(value);
    valueRef.current = value;
    const draftKeyRef = useRef(draftKey);
    draftKeyRef.current = draftKey;
    const onContentChangeRef = useRef(onContentChange);
    onContentChangeRef.current = onContentChange;

    // Holds autosave until the stored draft was read, so the editor's starting content cannot overwrite it.
    const draftReadPendingRef = useRef(false);

    const flushPendingDraft = useCallback((beforeUnload = false) => {
        if (draftReadPendingRef.current && !beforeUnload) {
            return;
        }

        if (draftSaveTimerRef.current) {
            clearTimeout(draftSaveTimerRef.current);
            draftSaveTimerRef.current = null;
        }

        const pendingDraft = pendingDraftRef.current;
        pendingDraftRef.current = null;
        if (pendingDraft) {
            writeDraft(pendingDraft.storageKey, pendingDraft.content, beforeUnload);
        }
    }, []);

    const applyStartingContent = useCallback((editor: EDTR) => {
        const initialContent =
            pendingReplacementContent.current ?? valueRef.current ?? "";
        pendingReplacementContent.current = null;

        applyEditorContent(editor, initialContent);
        if (contentRef) {
            contentRef.current = initialContent;
        }

        const currentDraftKey = draftKeyRef.current;
        if (!currentDraftKey) {
            return;
        }

        // Drafts are read from IndexedDB, so a stored draft replaces the starting content a moment later.
        draftReadPendingRef.current = true;
        void readEditorDraft(getReactQuillDraftStorageKey(currentDraftKey)).then((storedDraft) => {
            draftReadPendingRef.current = false;

            if (editorRef.current === editor && draftKeyRef.current === currentDraftKey) {
                const draftContent = resolveDraftToRestore(
                    currentDraftKey,
                    initialContent,
                    storedDraft,
                );
                if (draftContent !== null) {
                    applyEditorContent(editor, draftContent);
                    if (contentRef) {
                        contentRef.current = draftContent;
                    }
                    onContentChangeRef.current?.(draftContent);
                }
            }

            if (pendingDraftRef.current) {
                flushPendingDraft();
            }
        });
    }, [contentRef, flushPendingDraft]);


    const scheduleDraftSave = useCallback((content: string) => {
        if (!draftKey) {
            return;
        }

        if (draftSaveTimerRef.current) {
            clearTimeout(draftSaveTimerRef.current);
        }

        pendingDraftRef.current = {
            storageKey: getReactQuillDraftStorageKey(draftKey),
            content,
        };
        draftSaveTimerRef.current = setTimeout(
            () => flushPendingDraft(),
            DRAFT_AUTOSAVE_DELAY_MS,
        );
    }, [draftKey, flushPendingDraft]);

    if (initialValueCacheIdentifierRef.current !== cacheIdentifier) {
        initialValueCacheIdentifierRef.current = cacheIdentifier;
        initialValueRef.current = value ?? "";
    }

    useEffect(() => {
        const nextValue = value ?? "";
        const previousValue = lastValue.current;
        lastValue.current = nextValue;

        if (contentRef) {
            contentRef.current = nextValue;
        }

        if (!editorRef.current) {
            return;
        }

        const currentContent = editorRef.current.getContent();
        const currentNormalizedContent = normalizeTinyMceContent(currentContent);
        const nextNormalizedContent = normalizeTinyMceContent(nextValue);
        const previousNormalizedContent = normalizeTinyMceContent(previousValue);
        const shouldSyncEditorContent =
            currentNormalizedContent !== nextNormalizedContent &&
            (!editorRef.current.isDirty() ||
                currentNormalizedContent === previousNormalizedContent ||
                currentNormalizedContent === "");

        if (shouldSyncEditorContent) {
            editorRef.current.setContent(nextValue);
        }
    }, [contentRef, value]);

    useEffect(() => {
        if (!replaceContent) {
            return;
        }

        pendingReplacementContent.current = replaceContent.content ?? "";
        if (editorRef.current) {
            editorRef.current.setContent(pendingReplacementContent.current);
            if (contentRef) {
                contentRef.current = pendingReplacementContent.current;
            }
            pendingReplacementContent.current = null;
        }
    }, [replaceContent, contentRef]);

    useEffect(() => {
        if (!editorRef.current) {
            return;
        }

        editorRef.current.mode.set(disabled ? "readonly" : "design");
    }, [disabled]);

    useEffect(() => {
        if (editorRef.current) {
            setEditorSurfaceStyle(
                editorRef.current,
                backgroundColor,
                editorMinHeight,
            );
        }
    }, [backgroundColor, editorMinHeight]);

    useEffect(() => {
        return () => {
            if (editorRef.current) {
                editorRef.current.off(); // removes all event listeners
                editorRef.current.remove(); // removes the editor instance
                editorRef.current = null;
            }

            if (contentRef) {
                contentRef.current = null;
            }
        };
    }, [contentRef]);

    useEffect(() => {
        const handlePageHide = () => flushPendingDraft(true);
        window.addEventListener("pagehide", handlePageHide);

        return () => {
            window.removeEventListener("pagehide", handlePageHide);
            flushPendingDraft();
        };
    }, [draftKey, flushPendingDraft]);

    return (
        <div
            id={`react-quill-isolated-${cacheIdentifier}-outer`}
            className={`react-quill-isolated ${className ?? ""}`.trim()}
        >
            <DocumentCard
                key={`react-quill-isolated-${cacheIdentifier}-card`}
                id={cacheIdentifier}
                style={{
                    position: "relative",
                    minWidth: "calc(100% - 2px)",
                    borderColor: originalMessageColor,
                    userSelect: "text",
                    backgroundColor: backgroundColor,
                }}
            >
                <div
                    id={`react-quill-isolated-${cacheIdentifier}-toolbar`}
                    className="react-quill-isolated-toolbar"
                />
                <div
                    className="react-quill-isolated-editor-surface"
                    style={{backgroundColor}}
                >
                    <Editor
                        disabled={disabled}
                        onBlur={(evt, editor) => {
                            flushPendingDraft();
                            if (onBlurContent) {
                                onBlurContent(contentRef?.current ?? editor.getContent() ?? null);
                            }
                        }}
                        onInit={(evt, editor) => {
                            if (editorRef) {
                                editorRef.current = editor;
                            }

                            setEditorSurfaceStyle(
                                editor,
                                backgroundColor,
                                editorMinHeight,
                            );

                            editor.on("keydown", function (e) {
                                if (e.keyCode === 9) {
                                    // TAB key
                                    e.preventDefault();
                                }
                            });
                        }}
                        id={`react-quill-isolated-${cacheIdentifier}`}
                        inline
                        onSaveContent={(evt, editor) => {
                        }}
                        initialValue={initialValueRef.current}
                        key={cacheIdentifier}
                        onEditorChange={(v) => {
                            if (contentRef) {
                                contentRef!.current = v;
                            }
                            scheduleDraftSave(v ?? "");
                            if (onContentChange) {
                                onContentChange(v ?? null);
                            }
                        }}
                        init={{
                            highlight_on_focus: false,
                            // Preserve pasted links; relative URLs resolve against the app's root <base>.
                            convert_urls: false,
                            min_height: editorMinHeight,
                            inline_styles: false,
                            promotion: false,
                            fixed_toolbar_container: `#react-quill-isolated-${cacheIdentifier}-toolbar`,
                            toolbar_persist: true,
                            toolbar_mode: "wrap",
                            plugins: [
                                "advlist",
                                "autolink",
                                "lists",
                                "link",
                                "image",
                                "charmap",
                                "preview",
                                "anchor",
                                "searchreplace",
                                "visualblocks",
                                "code",
                                "fullscreen",
                                "insertdatetime",
                                "media",
                                "table",
                                "help",
                                "wordcount",
                                "emoticons",
                            ],
                            toolbar: `undo redo | formatselect |
                    bold italic backcolor | alignleft aligncenter
                    alignright alignjustify | bullist numlist outdent indent |
                    removeformat | image | code | help | emoticons`,
                            lists_indent_on_tab: true,
                            paste_data_images: true,
                            images_upload_handler: tinyMceInlineImageUploadHandler,
                            // tinymce-react resets the content to initialValue after onInit
                            // and before this callback, so starting content is applied here.
                            init_instance_callback: applyStartingContent,
                        }}
                    />
                </div>
                {hiddenInputProps && contentRef && (
                    <input
                        type="hidden"
                        {...hiddenInputProps}
                        value={contentRef?.current}
                    />
                )}
            </DocumentCard>
        </div>
    );
};
